/**
 * What a card in a work grid is drawn from.
 *
 * A grid of work draws the app's own cards — a capsule's and a book's — and both
 * of those are drawn from a *post*: the post is what the feed reads, what a thread
 * answers and what a card plays. What the post does not carry is the work's own
 * fields, so every screen that draws a grid holds a little table of them beside the
 * post, and this is the shape of that table.
 *
 * It is deliberately the shape a classroom's stored entry already has (see
 * `#/state/classrooms/types`) rather than a second one invented for the profile: a
 * classroom entry *is* an entry in a work grid, plus the room it belongs to. The
 * classroom's `ClassroomContent` satisfies this type as it stands, and a profile
 * builds one from the author's own records — which is what lets one set of cells
 * draw both screens.
 *
 * What it does *not* carry is which kind of work it is. A cell already knows that —
 * it is the capsule's card or the book's — and a grid holds one kind, so a `kind`
 * field here would be a second answer to a question the component was handed the
 * answer to. (The one place that needs to know is the *body* of the grid, which
 * decides what to draw from the tab it is filling; see `WorkKind`.)
 *
 * A **module** is the third kind, and it is the one case where a grid holds two:
 * a playlist is a kind of work and it also belongs *with* the capsules it gathers,
 * so the profile's Capsules tab draws both and lets the reader choose (see
 * `#/components/work/WorkKindSwitch`). The entry carries the module's two extra
 * fields for that reason and no other — the cells still ask the tab, not the entry,
 * what to draw.
 */
export type WorkKind = 'capsule' | 'flashbook' | 'module'

export type WorkEntry = {
  /** The entry's own id: the room's entry id, or the record's address. */
  id: string
  /** The body of the work, used for a title when the record has none. */
  text: string
  /** The capsule's own title, when this entry is a capsule. */
  capsuleTitle?: string
  /** The post everything that draws work reads. Absent for work never published. */
  postUri?: string
  /** The capsule record, when this entry is a capsule at all. */
  capsuleUri?: string
  /** The book's own title, when this entry is a book. */
  flashbookTitle?: string
  /** The book record, when this entry is a book at all. */
  flashbookUri?: string
  /** The module's own title, when this entry is a playlist. */
  moduleTitle?: string
  /**
   * The module record, when this entry is a playlist.
   *
   * A module with no post is a draft, so — as for a book — this comes with a post
   * or it is drawn by the draft cell; there is no third state to draw.
   */
  moduleUri?: string
  authorDid: string
  authorHandle: string
  authorName?: string
  authorAvatar?: string
  createdAt: string
}

/** The capsule card's proportions, which are the film's. */
export const CAPSULE_ASPECT = 9 / 16

/**
 * A book card's cover proportions, as the feed draws them.
 *
 * The same 4:5 the card itself uses (`FlashBookFeedEmbed`), so a cell waiting for
 * its post is the size of the card that will replace it and the grid does not jump.
 * A capsule keeps 9:16, which is the proportion of *its* card.
 */
export const BOOK_ASPECT = 4 / 5

/**
 * A module card's proportions, as the grid draws them.
 *
 * A playlist has no cover to take a shape from — its card is a stack of the
 * capsules it gathers — so it borrows the *book's* proportion rather than the
 * capsule's. Two reasons, and both are about the grid rather than the module: a
 * playlist is nearer a book than a film (a rectangle of arranged work), and the
 * Capsules tab is the one grid that draws two kinds side by side, where a 9:16
 * card next to a 4:5 one would make the module look like a mistake.
 */
export const MODULE_ASPECT = BOOK_ASPECT

/**
 * The proportions a grid cell for this kind of work is drawn at.
 *
 * One function rather than three constants read in three places: the cells that
 * stand in for work (a placeholder, an empty box, a draft) all have to reserve the
 * *same* space the card will take, or the grid jumps the moment the cards arrive — and
 * a cell that picked its own proportion would be a cell that jumped.
 */
export function workAspect(kind: WorkKind): number {
  return kind === 'capsule' ? CAPSULE_ASPECT : BOOK_ASPECT
}
